/**
 * Labstep
 *
 * @module models/ProtocolCondition
 * @desc Typescript export class for ProtocolCondition
 */

import { Type } from 'class-transformer';
import type {
  LockEntityInfo,
  PermissionEntityInfo,
} from 'labstep-web/models';
import { Entity } from 'labstep-web/models/entity.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { Resource } from 'labstep-web/models/resource.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';

export type ProtocolConditionEntityNameType = 'protocol_condition';

export class ProtocolCondition extends Entity {
  public static readonly idAttr = 'guid' as const;

  public static readonly entityName: ProtocolConditionEntityNameType =
    'protocol_condition';

  public get entityName(): ProtocolConditionEntityNameType {
    return ProtocolCondition.entityName;
  }

  public constructor(data: Partial<ProtocolCondition> = {}) {
    super();
    Object.assign(this, data);
  }

  public guid!: string;

  @Type(() => ProtocolValue)
  public protocol_values!: ProtocolValue[];

  @Type(() => ProtocolValue)
  public protocol_values_variable!: ProtocolValue[];

  @Type(() => ProtocolValue)
  public protocol_values_constant!: ProtocolValue[];

  @Type(() => Metadata)
  public metadatas!: Metadata[];

  @Type(() => Metadata)
  public metadatas_variable!: Metadata[];

  @Type(() => Metadata)
  public metadatas_constant!: Metadata[];

  @Type(() => Experiment)
  public experiment!: Experiment;

  @Type(() => Protocol)
  public protocol!: Protocol;

  public is_experiment_child!: boolean;

  public get nVariables(): number {
    return this.protocol_values.length + this.metadatas.length;
  }

  public get lock_entity_info(): LockEntityInfo | undefined {
    if (this._lock_entity_info) {
      return this._lock_entity_info;
    }
    if (this.experiment) {
      return {
        entityName: 'experiment',
        id: this.experiment.id,
      };
    }
    if (this.protocol) {
      return {
        entityName: 'protocol',
        id: this.protocol.id,
      };
    }
    return undefined;
  }

  public set lock_entity_info(lockEntityInfo:
    | LockEntityInfo
    | undefined) {
    this._lock_entity_info = lockEntityInfo;
  }

  public get permission_entity_info():
    | PermissionEntityInfo
    | undefined {
    if (this._permission_entity_info) {
      return this._permission_entity_info;
    }
    if (this.experiment) {
      return {
        entityName: 'experiment',
        id: this.experiment.id,
      };
    }
    if (this.protocol) {
      return {
        entityName: 'protocol',
        id: this.protocol.id,
      };
    }
    return undefined;
  }

  public set permission_entity_info(permissionEntityInfo:
    | PermissionEntityInfo
    | undefined) {
    this._permission_entity_info = permissionEntityInfo;
  }

  public static getHumanReadableEntityName(
    plural?: boolean,
    capitalized?: boolean,
  ): string {
    return getHumanReadableEntityName(
      this.entityName,
      plural,
      capitalized,
    );
  }
}
